/* eslint-disable react/jsx-no-duplicate-props */
/* eslint-disable react/no-array-index-key */
/* eslint-disable no-nested-ternary */
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardMedia,
  Chip,
  Container,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { styled } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { SerializedError } from '@reduxjs/toolkit';
import {
  formatCurrency,
  formatList,
} from 'renderer/shared/formatters/formatters';
import { useState, useEffect } from 'react';
import { useAppSelector } from 'renderer/shared/state/hooks';
import { useGoogleAds } from 'renderer/shared/analytics';
import { selectUserRole } from 'renderer/features/auth/state/userSelectors';
import apiSlice from 'renderer/shared/state/apiSlice';
import { transform } from 'lodash';
import AddToCartConfirmationModal from './AddToCartConfirmationModal';
import {
  useGetProductDetailsQuery,
  useListProductsQuery,
  useValidateCrossgradeSerialMutation,
  useValidateUpgradeSerialMutation,
} from '../services/products.endpoints';
import {
  useGetCartQuery,
  useAddToCartMutation,
} from '../../payments-page/services/cart.endpoints';

import { GetProductDetailsResponse } from '../schemas/products.schemas';
import productThumbnails from '../constants/productThumbnails';
import ProductDeleteDialog from './ProductDeleteDialog';
import PurchaseDialog from './PurchaseDialog';
import PurchaseDialogForProductDetailsPage from './PurchaseDialogForProductDetailsPage';
import FormattedDescription from './FormattedDescription';
import AuthorizeDialog from './AuthorizeDialog';
import { getProductStorePath, isMongoProductId } from '../utils/productPath';

const baseUrl = `${process.env.SERVER_URL ?? 'http://localhost:3000'}/api`;
const TopBanner = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  backgroundColor: '#D32F2F',
  color: 'white',
  fontWeight: 'bold',
  textAlign: 'center',
  padding: '4px 0',
  fontSize: '0.85rem',
  zIndex: 20,
}));

const DiagonalNewBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 12,
  left: -30,
  transform: 'rotate(-45deg)',
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  fontSize: '0.7rem',
  fontWeight: 'bold',
  padding: '4px 40px',
  zIndex: 30,
  boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
  pointerEvents: 'none',
}));

const NewBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 8,
  left: 0,
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  fontSize: '0.85rem',
  fontWeight: 'bold',
  padding: '4px 10px',
  borderRadius: 2,
  zIndex: 30,
  boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
}));

const LoadingCard = () => {
  return (
    <Grid container spacing={3}>
      <Grid item xs={4}>
        <Skeleton variant="rounded" data-testid="product-loading" />
      </Grid>
      <Grid item container spacing={3} direction="column" xs={8}>
        <Grid item>
          <Typography variant="h2">
            <Skeleton />
          </Typography>
        </Grid>
        <Grid item>
          <Typography variant="h4">
            <Skeleton />
          </Typography>
        </Grid>
        <Grid item>
          <Skeleton />
        </Grid>
      </Grid>
      <Grid item>
        <Skeleton />
      </Grid>
    </Grid>
  );
};

interface ErrorCardProps {
  error: FetchBaseQueryError | SerializedError;
}

const ErrorCard = ({ error }: ErrorCardProps) => {
  return (
    <Card>
      <CardHeader title="Error loading product details" />
      <CardContent>
        <Typography>
          {'status' in error && `${error.status}: ${error.data}`}
        </Typography>
      </CardContent>
    </Card>
  );
};

interface ProductDetailsProps {
  details: GetProductDetailsResponse;
  id: string;
}

interface CartItem {
  productId: {
    _id: string;
    productId: string;
    name: string;
    fullPrice: number;
  };
  quantity: number;
  platform?: string;
  hostApp?: {
    id: string;
    displayName: string;
    version: string;
    _id?: string;
  };
}

export interface HostApp {
  id: string; // internal stable ID (ex: com.digitalanarchy.Anarchy.AfterEffects)
  displayName: string; // human-facing name (ex: "After Effects")
  version: string; // version number as string ("1.0", "2.0")
  _id?: string; // optional MongoDB ObjectId (if populated)
  priceOverride?: number; // optional: only used for Video Plugins, may not exist for upgrades
}

interface UpgradeRow {
  serial: string;
  hostApp: HostApp | null; // auto-filled after validation
  sourceHostApp: HostApp | null;
  targetHostApp: HostApp | null;
  platform: string; // dropdown
  status: 'idle' | 'valid' | 'invalid' | 'loading';
  error?: string;
}

const ProductDetails = ({ details, id }: ProductDetailsProps) => {
  const [upgradeRows, setUpgradeRows] = useState<UpgradeRow[]>([
    {
      serial: '',
      hostApp: null,
      sourceHostApp: null,
      targetHostApp: null,
      platform: 'All Platforms',
      status: 'idle',
    },
  ]);

  // important: one serial per quantity
  const [upgradeSerials, setUpgradeSerials] = useState<string[]>(['']);

  const [cartError, setCartError] = useState<string | null>(null);
  const [upgradeKey, setUpgradeKey] = useState('');
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [hostAppError, setHostAppError] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const role = useAppSelector(selectUserRole);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(
    null,
  );
  const [triggerAddToCartAfterDialog, setTriggerAddToCartAfterDialog] =
    useState(false);
  const [platform, setPlatform] = useState<string>('Windows');
  // const [hostApp, setHostApp] = useState<string>('');
  const [hostApp, setHostApp] = useState<{
    id: string;
    displayName: string;
    version: string;
    priceOverride?: number | '';
  } | null>(null);

  const [quantity, setQuantity] = useState<number>(1);

  const [addToCart] = useAddToCartMutation();
  // const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [cartItems, setCartItems] = useState([]);
  const { refetch } = useGetCartQuery(null);

  useEffect(() => {
    setUpgradeRows((rows) => {
      const newRows = [...rows];

      if (quantity > newRows.length) {
        while (newRows.length < quantity) {
          newRows.push({
            serial: '',
            hostApp: null,
            sourceHostApp: null,
            targetHostApp: null,
            platform: 'All Platforms',
            status: 'idle',
          });
        }
      } else if (quantity < newRows.length) {
        newRows.length = quantity;
      }
      return newRows;
    });
  }, [quantity]);

  // Canonicalize legacy Mongo ID URLs to stable slug when a product family is set.
  useEffect(() => {
    if (!details?.stableSlug || !id) return;
    if (role === 'admin') return;
    if (!isMongoProductId(id)) return;

    const canonicalPath = getProductStorePath(details);
    if (canonicalPath !== `/products/${id}`) {
      navigate(canonicalPath, { replace: true });
    }
  }, [details, id, role, navigate]);

  const {
    data: products,
    isLoading,
    error,
  } = useListProductsQuery({ includeHidden: role === 'admin' });

  const {
    _id,
    name,
    fullDescription,
    briefDescription,
    productId,
    productPage,
    downloadPage,
    hidden,
    keywords,
    category,
    supportedHostApplications,
    supportedPlatforms,
    startingPrice,
    salePrice,
  } = details;

  const isUpgrade = category === 'Upgrade';
  const isCrossgrade = isUpgrade && name.toLowerCase().includes('crossgrade');

  const image = details.productImageId
    ? `${baseUrl}/product-images/${details.productImageId}`
    : ''; // fallback placeholder
  const isSubscription = category === 'Subscription';

  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [cartSummary, setCartSummary] = useState({
    productName: name,
    productImage: image,
    platform,
    hostApplication: hostApp,
    quantity,
    subtotal: 0,
    totalItems: 0,
  });

  const [validateUpgradeSerial, upgradeResult] =
    useValidateUpgradeSerialMutation();
  const [validateCrossgradeSerial, crossgradeResult] =
    useValidateCrossgradeSerialMutation();

  useEffect(() => {
    if (supportedPlatforms?.length > 0) {
      setPlatform(supportedPlatforms[0]);
    }

    // // Auto-select first supported host app for all products
    // if (supportedHostApplications?.length > 0) {
    //   setHostApp(supportedHostApplications[0]);
    //   setHostAppError(false);
    // }
    if (!isUpgrade && supportedHostApplications?.length > 0) {
      setHostApp(supportedHostApplications[0]);
      setHostAppError(false);
    }
  }, [supportedPlatforms, supportedHostApplications, isUpgrade]);

  if (!id) return null;

  // const validateSerial = async (idx: number, serial: string) => {
  const validateSerial = async (
    idx: number,
    serial: string,
    targetHostAppIdOverride?: string,
  ) => {
    const trimmed = serial.trim();

    // 1. EMPTY → reset everything for this row
    if (!trimmed) {
      setUpgradeRows((rows) => {
        const copy = [...rows];
        copy[idx] = {
          ...copy[idx],
          status: 'idle',
          error: undefined,
          hostApp: null,
          sourceHostApp: null,
          targetHostApp: null,
        };
        return copy;
      });
      return;
    }

    // 2. DUPLICATE CHECK
    const duplicateInOrder = upgradeRows.some(
      (r, i) =>
        i !== idx && r.serial.trim().toLowerCase() === trimmed.toLowerCase(),
    );

    if (duplicateInOrder && !isCrossgrade) {
      setUpgradeRows((rows) => {
        const copy = [...rows];
        const prev = copy[idx];
        copy[idx] = {
          ...prev,
          status: 'invalid',
          error: 'This serial number is already used in this order.',
          sourceHostApp: null,
          hostApp: prev.hostApp,
          targetHostApp: prev.targetHostApp,
        };
        return copy;
      });
      return;
    }

    // 3. LOADING
    setUpgradeRows((rows) => {
      const copy = [...rows];
      copy[idx] = {
        ...copy[idx],
        status: 'loading',
        error: undefined,
      };
      return copy;
    });

    try {
      let response;

      // 4. VALIDATE (no targetHostApp involved)
      if (isCrossgrade) {
        response = await validateCrossgradeSerial({
          serial: trimmed,
          productId: id,
          // targetHostAppId: upgradeRows[idx].targetHostApp?.id,
          targetHostAppId:
            targetHostAppIdOverride ?? upgradeRows[idx].targetHostApp?.id,
        }).unwrap();
      } else {
        response = await validateUpgradeSerial({
          serial: trimmed,
          productId: id,
        }).unwrap();
      }

      // 5. INVALID SERIAL — keep user-picked host tier; clear API-derived source only
      if (!response.valid) {
        setUpgradeRows((rows) => {
          const copy = [...rows];
          const prev = copy[idx];
          copy[idx] = {
            ...prev,
            status: 'invalid',
            error: response.reason || 'Invalid serial.',
            sourceHostApp: null,
            ...(isCrossgrade
              ? {
                  hostApp: null,
                  targetHostApp: prev.targetHostApp,
                }
              : {
                  hostApp: prev.hostApp,
                  targetHostApp: null,
                }),
          };
          return copy;
        });
        return;
      }

      // 6. SUCCESS
      setUpgradeRows((rows) => {
        const copy = [...rows];
        const prev = copy[idx];
        copy[idx] = {
          ...prev,
          status: 'valid',
          error: undefined,

          // Preserve user-selected target host; fall back to validated host
          hostApp: prev.hostApp ?? response.hostApp ?? null,

          // crossgrades use sourceHostApp (locked from serial)
          sourceHostApp: response.hostApp || null,

          // IMPORTANT: do NOT touch targetHostApp here
          // user choice must persist unless serial changes
        };
        return copy;
      });
    } catch {
      // 7. HARD FAILURE — preserve user host selection like invalid response
      setUpgradeRows((rows) => {
        const copy = [...rows];
        const prev = copy[idx];
        copy[idx] = {
          ...prev,
          status: 'invalid',
          error: 'Invalid serial.',
          sourceHostApp: null,
          ...(isCrossgrade
            ? {
                hostApp: null,
                targetHostApp: prev.targetHostApp,
              }
            : {
                hostApp: prev.hostApp,
                targetHostApp: null,
              }),
        };
        return copy;
      });
    }
  };

  const toHostAppPayload = (ha: typeof hostApp | null) => {
    if (!ha) return undefined;
    const priceOverride =
      typeof ha.priceOverride === 'number' ? ha.priceOverride : undefined;
    return {
      id: ha.id,
      displayName: ha.displayName,
      version: ha.version,
      ...(priceOverride !== undefined ? { priceOverride } : {}),
    };
  };

  const handlePurchaseClick = async (
    pid: string,
    productCategory: string,
    productName: string,
  ) => {
    // -------------------------
    // VALIDATION
    // -------------------------
    if (isUpgrade) {
      const allFilled = upgradeRows.every((r) => r.serial.trim() !== '');
      const allValid = upgradeRows.every((r) => r.status === 'valid');

      if (!allFilled) {
        setCartError('Please enter all serial numbers.');
        return;
      }

      if (!allValid) {
        setCartError('One or more serial numbers are invalid.');
        return;
      }

      if (isCrossgrade) {
        const missingTargets = upgradeRows.some((r) => !r.targetHostApp);

        if (missingTargets) {
          setCartError(
            'Please select a target host application for each serial.',
          );
          return;
        }

        // const sameHostCrossgrade = upgradeRows.some(
        //   (r) =>
        //     r.sourceHostApp &&
        //     r.targetHostApp &&
        //     familyPrefix(r.sourceHostApp.id) ===
        //       familyPrefix(r.targetHostApp.id),
        // );

        // if (sameHostCrossgrade) {
        //   setCartError('Crossgrade must be for a different host application.');
        //   return;
        // }
      }
    }

    // Normal product requires a target hostApp
    if (!isUpgrade && !hostApp) {
      setHostAppError(true);
      return;
    }

    setCartError(null);

    // -------------------------
    // VIDEO PLUGIN UPSELL
    // -------------------------
    if (
      productCategory === 'Video Plugin' &&
      !productName.toLowerCase().includes('anarchy complete bundle')
    ) {
      setSelectedProductId(pid);
      setModalOpen(true);
      return;
    }

    // -------------------------
    // PAYLOAD BUILD
    // -------------------------
    const payload: any = {
      productId: pid,
      quantity,
      platform,
    };

    if (isUpgrade) {
      payload.serials = upgradeRows.map((r) => r.serial.trim());

      payload.serialMap = upgradeRows.map((r) => {
        // ✅ CROSSGRADE: explicit source → target mapping
        if (isCrossgrade) {
          return {
            serial: r.serial.trim(),
            sourceHostApp: {
              id: r.sourceHostApp!.id,
              displayName: r.sourceHostApp!.displayName,
              version: r.sourceHostApp!.version,
            },
            targetHostApp: {
              id: r.targetHostApp!.id,
              displayName: r.targetHostApp!.displayName,
              version: r.targetHostApp!.version,
              ...(typeof r.targetHostApp!.priceOverride === 'number'
                ? { priceOverride: r.targetHostApp!.priceOverride }
                : {}),
            },
          };
        }

        // ✅ REGULAR UPGRADE (existing behavior preserved)
        return {
          serial: r.serial.trim(),
          hostApp: r.hostApp
            ? {
                id: r.hostApp.id,
                displayName: r.hostApp.displayName,
                version: r.hostApp.version,
                ...(typeof r.hostApp.priceOverride === 'number'
                  ? { priceOverride: r.hostApp.priceOverride }
                  : {}),
              }
            : null,
        };
      });
    } else {
      // ✅ Normal plugin / subscription
      payload.hostApplication = toHostAppPayload(hostApp);
    }

    console.log('PAYLOAD', payload);

    // -------------------------
    // SUBMIT
    // -------------------------
    try {
      await addToCart(payload).unwrap();
      navigate('/purchase');
    } catch (err: any) {
      const msg =
        err?.data?.message ||
        err?.error ||
        'Could not proceed to checkout. Please verify your serial(s).';
      setCartError(msg);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedProductId(null);
  };

  const priceDelta = (ha: any) =>
    typeof ha?.priceOverride === 'number' ? ha.priceOverride : 0;

  const effectiveUnitBase = (item: any) => {
    const sale = item.productId?.sale;
    const hasSale =
      sale?.salePrice != null &&
      (typeof sale.until === 'number'
        ? sale.until > Date.now()
        : sale?.until
        ? new Date(sale.until).getTime() > Date.now()
        : false);

    return hasSale ? sale.salePrice : item.productId.fullPrice;
  };

  const handleAddToCart = async () => {
    // -------------------------
    // VALIDATION
    // -------------------------
    if (isUpgrade) {
      const allFilled = upgradeRows.every((r) => r.serial.trim() !== '');
      const allValid = upgradeRows.every((r) => r.status === 'valid');

      if (!allFilled) {
        setCartError('Please enter all serial numbers.');
        return;
      }

      if (!allValid) {
        setCartError('One or more serial numbers are invalid.');
        return;
      }

      if (isCrossgrade) {
        const missingTargets = upgradeRows.some((r) => !r.targetHostApp);

        if (missingTargets) {
          setCartError(
            'Please select a target host application for each serial.',
          );
          return;
        }
      }
    }

    if (!isUpgrade && !hostApp) {
      setHostAppError(true);
      return;
    }

    // -------------------------
    // VIDEO PLUGIN UPSELL
    // -------------------------
    if (category === 'Video Plugin') {
      setSelectedProductId(_id);
      setTriggerAddToCartAfterDialog(true);
      setModalOpen(true);
      return;
    }

    setHostAppError(false);

    // -------------------------
    // PAYLOAD BUILD
    // -------------------------
    const payload: any = {
      productId: _id,
      quantity,
      platform,
    };

    if (isUpgrade) {
      payload.serials = upgradeRows.map((r) => r.serial.trim());

      payload.serialMap = upgradeRows.map((r) => {
        // ✅ CROSSGRADE — explicit source → target
        if (isCrossgrade) {
          return {
            serial: r.serial.trim(),
            sourceHostApp: {
              id: r.sourceHostApp!.id,
              displayName: r.sourceHostApp!.displayName,
              version: r.sourceHostApp!.version,
            },
            targetHostApp: {
              id: r.targetHostApp!.id,
              displayName: r.targetHostApp!.displayName,
              version: r.targetHostApp!.version,
              ...(typeof r.targetHostApp!.priceOverride === 'number'
                ? { priceOverride: r.targetHostApp!.priceOverride }
                : {}),
            },
          };
        }

        // ✅ REGULAR UPGRADE (unchanged behavior)
        return {
          serial: r.serial.trim(),
          hostApp: r.hostApp
            ? {
                id: r.hostApp.id,
                displayName: r.hostApp.displayName,
                version: r.hostApp.version,
                ...(typeof r.hostApp.priceOverride === 'number'
                  ? { priceOverride: r.hostApp.priceOverride }
                  : {}),
              }
            : null,
        };
      });
    } else {
      // ✅ Normal plugin / subscription
      payload.hostApplication = toHostAppPayload(hostApp);
    }

    // -------------------------
    // SUBMIT
    // -------------------------
    try {
      const updatedCart = await addToCart(payload).unwrap();

      dispatch(apiSlice.util.invalidateTags([{ type: 'Cart' }]));

      const sortedItems = [
        ...updatedCart.items.filter((i: any) => i.productId === _id),
        ...updatedCart.items.filter((i: any) => i.productId !== _id),
      ];

      const subtotal = sortedItems.reduce((sum: number, item: any) => {
        const base = effectiveUnitBase(item);
        const delta =
          priceDelta(item.hostApplication) ||
          (item.productId === _id ? priceDelta(hostApp) : 0);

        return sum + (base + delta) * item.quantity;
      }, 0);

      setCartSummary({
        productName: name,
        productImage: image,
        platform,
        hostApplication: hostApp,
        quantity,
        subtotal,
        totalItems: sortedItems.reduce(
          (acc: number, i: any) => acc + i.quantity,
          0,
        ),
      });

      setCartItems(sortedItems as never[]);
      setConfirmationOpen(true);
    } catch (err: any) {
      const msg =
        err?.data?.message ||
        err?.error ||
        'Could not proceed to checkout. Please verify your serial(s).';
      setCartError(msg);
    }
  };

  const handleNavigate = async (path: string) => {
    console.log('handleNavigate', triggerAddToCartAfterDialog);
    if (triggerAddToCartAfterDialog) {
      setTriggerAddToCartAfterDialog(false);

      const updatedCart = await addToCart({
        productId: selectedProductId!,
        quantity,
        platform,
        hostApplication: hostApp,
      }).unwrap();

      dispatch(apiSlice.util.invalidateTags([{ type: 'Cart' }]));

      const sortedItems = [
        ...updatedCart.items.filter(
          (i: any) => i.productId === selectedProductId,
        ),
        ...updatedCart.items.filter(
          (i: any) => i.productId !== selectedProductId,
        ),
      ];

      const subtotal = sortedItems.reduce((sum, item) => {
        const basePrice =
          item.productId.sale?.salePrice != null &&
          item.productId.sale?.until > Date.now()
            ? item.productId.sale.salePrice
            : item.productId.fullPrice;

        const itemDelta =
          typeof item.hostApplication?.priceOverride === 'number'
            ? item.hostApplication.priceOverride
            : 0;

        const unitPrice = basePrice + itemDelta;
        return sum + unitPrice * item.quantity;
      }, 0);

      setCartSummary({
        productName: name,
        productImage: image,
        platform,
        hostApplication: hostApp,
        quantity,
        subtotal,
        totalItems: sortedItems.reduce((sum, i) => sum + i.quantity, 0),
      });

      setCartItems(sortedItems as never[]);
      setConfirmationOpen(true); // ✅ Show confirmation modal
      handleCloseModal();
      return;
    }

    navigate(path);
  };

  const delta =
    typeof hostApp?.priceOverride === 'number' ? hostApp.priceOverride : 0;

  const displayStartingPrice = (startingPrice ?? 0) + delta;
  const displaySalePrice = salePrice != null ? salePrice + delta : null;

  // keep your existing "isOnSale" logic, it still works fine:
  const isOnSale = displaySalePrice != null;

  console.log({ upgradeRows, upgradeSerials, quantity });
  // console.log(upgradeSerials.length);

  const upgradeRowsContainAllHostApps = upgradeRows.some((r) => {
    if (!isUpgrade) return false;

    if (isCrossgrade)
      return r.targetHostApp?.displayName.toLowerCase().includes('all');

    return r.hostApp?.displayName.toLowerCase().includes('all');
  });

  // console.log('upgradeRowsContainAllHostApps', upgradeRowsContainAllHostApps);
  // console.log({ details });
  let allHostApp = null;
  [allHostApp] = supportedHostApplications.filter(
    (s) => s.displayName.toLowerCase().includes('all'), // === 'All Host Apps',
  );
  console.log('allHostApp', allHostApp);

  return (
    <>
      <Grid container spacing={4}>
        <Grid item xs={12} md={5}>
          <Tooltip title={keywords}>
            <Card
              data-testid="product-keywords"
              elevation={0}
              sx={{ borderRadius: 0 }}
            >
              <Box
                position="relative"
                sx={{
                  border: '1px solid rgba(0,0,0,0.12)',
                  overflow: 'hidden', // ensures rounded corners clip the image
                }}
              >
                {/* ON SALE BANNER */}
                {isOnSale && <TopBanner>ON SALE!</TopBanner>}

                {/* NEW BADGE */}
                {details.showBadge && <NewBadge>NEW</NewBadge>}

                <CardMedia
                  component="img"
                  image={image}
                  alt={name}
                  height={320}
                  data-testid="product-image"
                  // sx={{ height: 800 }}
                  sx={{ objectFit: 'contain' }}
                />
              </Box>

              {role === 'admin' && (
                <CardActions>
                  <Box>
                    <Link to={`/products/edit/${_id}`}>
                      <Button variant="outlined" color="primary">
                        Edit
                      </Button>
                    </Link>
                  </Box>
                  <Box>
                    <Button
                      variant="contained"
                      color="error"
                      onClick={() => setDeleteDialogOpen(true)}
                    >
                      Delete
                    </Button>
                  </Box>
                  <ProductDeleteDialog
                    open={deleteDialogOpen}
                    setOpen={setDeleteDialogOpen}
                    id={id}
                  />
                </CardActions>
              )}
            </Card>
          </Tooltip>
        </Grid>

        <Grid item xs={12} md={7}>
          <Typography variant="h4" fontWeight="bold">
            {name}
          </Typography>

          {hidden && (
            <Tooltip title="This item is hidden from customers.">
              <VisibilityOffIcon color="action" data-testid="product-hidden" />
            </Tooltip>
          )}
          <Typography variant="h5" sx={{ fontWeight: 600, mt: 0.5 }}>
            {isOnSale ? (
              <>
                <span
                  style={{
                    textDecoration: 'line-through',
                    marginRight: 8,
                    opacity: 0.6,
                    fontWeight: 400,
                  }}
                >
                  {formatCurrency(displayStartingPrice)}
                </span>
                {formatCurrency(displaySalePrice!)}
              </>
            ) : (
              formatCurrency(displayStartingPrice)
            )}
            {isSubscription && ' / year'}
          </Typography>

          {isUpgrade &&
            upgradeRowsContainAllHostApps &&
            allHostApp &&
            allHostApp.priceOverride && (
              <Chip
                label={`+${formatCurrency(allHostApp.priceOverride)} - ${
                  allHostApp.displayName
                }`}
              />
            )}

          <Typography
            variant="subtitle1"
            sx={{ mb: 3, textTransform: 'capitalize' }}
          >
            {category}
          </Typography>

          <Typography variant="subtitle2" sx={{ mb: 3 }}>
            <FormattedDescription text={briefDescription} />
          </Typography>

          {cartError && (
            <Alert severity="error" sx={{ my: 2 }}>
              {cartError === 'NOT FOUND'
                ? 'Please enter a valid serial number.'
                : cartError}
            </Alert>
          )}

          {/* FORM SECTION */}
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2}>
              {category === 'Upgrade' ? (
                <>
                  {/* quantity + platform */}
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Quantity"
                      type="number"
                      fullWidth
                      size="small"
                      margin="dense"
                      inputProps={{ min: 1 }}
                      sx={{ backgroundColor: 'white', borderRadius: 1 }}
                      value={quantity}
                      onChange={(e) => setQuantity(Number(e.target.value))}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <FormControl
                      fullWidth
                      size="small"
                      margin="dense"
                      sx={{ backgroundColor: 'white', borderRadius: 1 }}
                    >
                      <InputLabel id="platform-label">Platform</InputLabel>
                      <Select
                        labelId="platform-label"
                        value={platform}
                        onChange={(e) => setPlatform(e.target.value)}
                        label="Platform"
                      >
                        {supportedPlatforms.map((p) => (
                          <MenuItem key={p} value={p}>
                            {p}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* CROSSGRADE ONLY — user selects TARGET host */}
                  <Grid item xs={12}>
                    <Stack>
                      {upgradeRows.map((row, idx) => (
                        <Grid container spacing={2} key={idx} sx={{ mb: 1 }}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Enter your Serial Number"
                              fullWidth
                              size="small"
                              value={row.serial}
                              onChange={(e) => {
                                const raw = e.target.value;
                                const trimmed = raw.trim();

                                // Update UI state
                                setUpgradeRows((prev) => {
                                  const copy = [...prev];
                                  copy[idx].serial = raw;
                                  return copy;
                                });

                                // If empty → reset & stop validation
                                if (trimmed === '') {
                                  setUpgradeRows((prev) => {
                                    const copy = [...prev];
                                    copy[idx] = {
                                      ...copy[idx],
                                      status: 'idle',
                                      error: undefined,
                                      hostApp: null,
                                      sourceHostApp: null,
                                      targetHostApp: null,
                                    };
                                    return copy;
                                  });
                                  return;
                                }

                                validateSerial(idx, trimmed);
                              }}
                              error={row.status === 'invalid'}
                              helperText={row.error}
                              sx={{ backgroundColor: 'white' }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            {isCrossgrade ? (
                              <Stack spacing={1}>
                                {/* TARGET (user choice) */}
                                <FormControl fullWidth size="small">
                                  <InputLabel id={`target-host-${idx}`}>
                                    Host Application
                                  </InputLabel>
                                  <Select
                                    labelId={`target-host-${idx}`}
                                    label="Target Host Application"
                                    value={row.targetHostApp?.id ?? ''}
                                    // onChange={(e) => {
                                    //   const selected =
                                    //     supportedHostApplications.find(
                                    //       (app) => app.id === e.target.value,
                                    //     );

                                    //   setUpgradeRows((prev) => {
                                    //     const copy = [...prev];
                                    //     copy[idx].targetHostApp =
                                    //       selected ?? null;
                                    //     return copy;
                                    //   });
                                    // }}
                                    onChange={(e) => {
                                      const selected =
                                        supportedHostApplications.find(
                                          (app) => app.id === e.target.value,
                                        ) ?? null;

                                      setUpgradeRows((prev) => {
                                        const copy = [...prev];
                                        copy[idx] = {
                                          ...copy[idx],
                                          targetHostApp: selected,
                                          status: copy[idx].serial
                                            ? 'loading'
                                            : 'idle',
                                          error: undefined,
                                        };
                                        return copy;
                                      });

                                      // ✅ IMPORTANT: pass the new ID explicitly
                                      const serial =
                                        upgradeRows[idx]?.serial?.trim();
                                      if (serial && selected?.id) {
                                        validateSerial(
                                          idx,
                                          serial,
                                          selected.id,
                                        );
                                      }
                                    }}
                                  >
                                    {supportedHostApplications.map((app) => (
                                      <MenuItem key={app.id} value={app.id}>
                                        {app.displayName}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Stack>
                            ) : (
                              // UPGRADE = ALLOW USER TO CHANGE AFTER VALIDATION
                              <FormControl
                                fullWidth
                                size="small"
                                // margin="dense"
                                sx={{
                                  backgroundColor: 'white',
                                  borderRadius: 1,
                                }}
                              >
                                <InputLabel id={`upgrade-hostapp-label-${idx}`}>
                                  Host Application
                                </InputLabel>
                                <Select
                                  labelId={`upgrade-hostapp-label-${idx}`}
                                  value={row.hostApp?.id ?? ''}
                                  label="Host Application"
                                  onChange={(e) => {
                                    const selected =
                                      supportedHostApplications.find(
                                        (app) => app.id === e.target.value,
                                      );

                                    // Update host app WITHOUT triggering revalidation
                                    setUpgradeRows((prev) => {
                                      const copy = [...prev];
                                      copy[idx].hostApp = selected
                                        ? {
                                            id: selected.id,
                                            displayName: selected.displayName,
                                            version: selected.version,
                                            ...(typeof selected.priceOverride ===
                                            'number'
                                              ? {
                                                  priceOverride:
                                                    selected.priceOverride,
                                                }
                                              : {}),
                                          }
                                        : null;
                                      return copy;
                                    });
                                  }}
                                >
                                  {supportedHostApplications.map((app) => (
                                    <MenuItem key={app.id} value={app.id}>
                                      {app.displayName}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            )}
                          </Grid>
                        </Grid>
                      ))}
                    </Stack>
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12} sm={4}>
                    <FormControl
                      fullWidth
                      size="small"
                      margin="dense"
                      sx={{ backgroundColor: 'white', borderRadius: 1 }}
                    >
                      <InputLabel id="platform-label">Platform</InputLabel>
                      <Select
                        labelId="platform-label"
                        value={platform}
                        onChange={(e) => setPlatform(e.target.value)}
                        label="Platform"
                      >
                        {supportedPlatforms.map((p) => (
                          <MenuItem key={p} value={p}>
                            {p}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <FormControl
                      fullWidth
                      size="small"
                      margin="dense"
                      sx={{ backgroundColor: 'white', borderRadius: 1 }}
                    >
                      <InputLabel id="hostApp-label">
                        Host Application
                      </InputLabel>
                      <Select
                        labelId="hostApp-label"
                        value={hostApp?.id ?? ''}
                        onChange={(e) => {
                          const selected = supportedHostApplications.find(
                            (app) => app.id === e.target.value,
                          );
                          setHostApp(selected ?? null);
                          setHostAppError(!selected);
                        }}
                        label="Host Application"
                      >
                        {supportedHostApplications.map((app) => (
                          <MenuItem key={app.id} value={app.id}>
                            {app.displayName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {hostAppError && (
                      <Alert severity="error" sx={{ mt: 1 }}>
                        Please select a host application.
                      </Alert>
                    )}
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Quantity"
                      type="number"
                      fullWidth
                      size="small"
                      margin="dense"
                      inputProps={{ min: 1 }}
                      sx={{ backgroundColor: 'white', borderRadius: 1 }}
                      value={quantity}
                      onChange={(e) => setQuantity(Number(e.target.value))}
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <Stack direction="row" spacing={2}>
                  <Button
                    onClick={(e) => {
                      e.preventDefault();
                      handlePurchaseClick(_id, category, name);
                    }}
                    variant="contained"
                    color="secondary"
                    size="large"
                    sx={{ flex: 1 }}
                    disabled={
                      isUpgrade
                        ? !upgradeRows.every(
                            (r) =>
                              r.serial.trim() !== '' &&
                              r.status === 'valid' &&
                              (!isCrossgrade || r.targetHostApp),
                          )
                        : !hostApp
                    }
                  >
                    Buy Now
                  </Button>

                  <Button
                    onClick={handleAddToCart}
                    variant="outlined"
                    size="large"
                    sx={{ flex: 1 }}
                    disabled={
                      isUpgrade
                        ? !upgradeRows.every(
                            (r) =>
                              r.serial.trim() !== '' &&
                              r.status === 'valid' &&
                              (!isCrossgrade || r.targetHostApp),
                          )
                        : !hostApp
                    }
                  >
                    Add to Cart
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      <Box
        sx={{
          marginTop: 0,
          mb: 3,
          px: { xs: 1, sm: 2 },
          py: { xs: 1, sm: 1.5 },
          backgroundColor: '#ECEFF1',
          borderRadius: 1,
          boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
          cursor: 'pointer',
          transition: 'background-color 0.2s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          '&:hover': {
            backgroundColor: '#CFD8DC',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          },
          '&:active': {
            backgroundColor: '#B0BEC5',
          },
          fontSize: { xs: '0.85rem', sm: '1rem' },
          textAlign: 'left',
        }}
        onClick={() => setAuthDialogOpen(true)}
        role="button"
        tabIndex={0}
        aria-label="Open authorization instructions"
      >
        <InfoOutlinedIcon
          sx={{ fontSize: '1.3rem', color: 'primary.main', flexShrink: 0 }}
        />
        <Typography sx={{ fontWeight: 500, fontSize: '0.95rem' }}>
          Need help authorizing a plugin or subscription? Click here for
          instructions.
        </Typography>
      </Box>

      <Box mt={4}>
        <Typography
          variant="subtitle1"
          sx={{ mb: 1, textTransform: 'capitalize' }}
        >
          Product Details
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
          <FormattedDescription text={fullDescription} />
        </Typography>

        <Typography sx={{ mt: 2 }}>
          Need more info and examples? Check out the{' '}
          <Link to={productPage} target="_blank" rel="noopener noreferrer">
            Product Page
          </Link>
        </Typography>

        <Typography>
          Want to give it a try?{' '}
          <Link to={downloadPage} target="_blank" rel="noopener noreferrer">
            Download a copy
          </Link>
          .
        </Typography>

        <Typography>
          Supported on <b>{formatList(supportedPlatforms)}</b>.
        </Typography>

        <Typography sx={{ mb: 4 }}>
          Use it in{' '}
          <b>
            {formatList(
              supportedHostApplications.map((app) => app.displayName),
            )}
          </b>
          .
        </Typography>
      </Box>

      <AddToCartConfirmationModal
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        {...cartSummary}
        items={cartItems}
      />

      <PurchaseDialogForProductDetailsPage
        products={products as any[]}
        open={modalOpen}
        onClose={handleCloseModal}
        onNavigate={handleNavigate}
        productId={selectedProductId}
        hostApp={hostApp}
        platform={platform}
        redirectToPurchasePage
        quantity={quantity}
        fromAddToCart={triggerAddToCartAfterDialog}
      />

      <AuthorizeDialog
        open={authDialogOpen}
        onClose={() => setAuthDialogOpen(false)}
      />
    </>
  );
};

const ProductDetailsPage = () => {
  useGoogleAds('base');

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const role = useAppSelector(selectUserRole);
  const {
    data: details,
    error,
    isLoading,
  } = useGetProductDetailsQuery(id ?? '');

  const hiddenRedirectTo =
    role !== 'admin' &&
    error &&
    'data' in error &&
    error.data &&
    typeof error.data === 'object' &&
    (error.data as { code?: string }).code === 'PRODUCT_HIDDEN'
      ? (error.data as { redirectTo?: string }).redirectTo || '/'
      : null;

  // Non-admins hitting a hidden PDP: follow API redirect to latest visible
  // sibling, or home when the family has no visible version.
  useEffect(() => {
    if (!hiddenRedirectTo) return;
    navigate(hiddenRedirectTo, { replace: true });
  }, [hiddenRedirectTo, navigate]);

  return (
    <Container maxWidth="md" sx={{ p: 0 }}>
      <Button
        component={Link}
        to="/"
        startIcon={<ArrowBackIcon />}
        variant="text"
        sx={{ mb: 3 }}
      >
        Back to All Products
      </Button>

      {isLoading && <LoadingCard />}
      {error && !hiddenRedirectTo && <ErrorCard error={error} />}
      {details && <ProductDetails details={details} id={id ?? ''} />}
    </Container>
  );
};

export default ProductDetailsPage;
